import { faChevronLeft, faLocationDot } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { cva } from 'class-variance-authority';
import { htmlStringToDocument } from 'contentful-rich-text-html-parser';
import { useTranslation } from 'next-i18next';
import React, { useEffect, useState } from 'react';

import { CHANNEL, vacanciesSlugs } from '@boss/constants/corporate-jobs';
import { RichText } from '@boss/rich-text';
import { IIVacancy, SectionFields } from '@boss/types/corporate-jobs';
import { Button, Image, Link, SocialShare, Video } from '@boss/ui';

import LocationMap from './LocationMap';
import JobDetailPromotionCta from '../../components/JobDetailPromotionCta';
import ComponentMapper from '../../mappers/ComponentMapper';

type Props = {
  vacancy: IIVacancy;
  locale: string;
  enrichedSections?: SectionFields[] | null;
  aboutSections?: SectionFields[] | null;
  spontaneousApplicationLink: string | null;
};

type ButtonProps = {
  sollicitatieLink: string;
  className?: string;
};

const ButtonStyling = cva('my-8 block w-fit ', {
  variants: {
    variant: {
      b2c: '',
      b2b: 'uppercase p-4',
    },
  },
});

const MainDivStyling = cva('w-full', {
  variants: {
    hasAsset: {
      true: 'md:w-2/3',
    },
  },
});

const AssetDivStyling = cva('w-full flex flex-col items-start gap-4', {
  variants: {
    hasAsset: {
      true: 'w-full md:w-1/3 md:mt-5',
    },
  },
});

const ApplyButton = ({ sollicitatieLink, className }: ButtonProps) => {
  const { t } = useTranslation(['jobs']);

  return (
    <Button
      anchorClassName={className}
      className={ButtonStyling({ variant: CHANNEL === 'jobs-b2c' ? 'b2c' : 'b2b' })}
      href={sollicitatieLink}
      label={t('applyButtonDetail')}
      type={CHANNEL === 'jobs-b2c' ? 'primary' : 'tertiary'}
    />
  );
};

const JobsDetailPage = ({ vacancy, locale, enrichedSections, aboutSections, spontaneousApplicationLink }: Props) => {
  const {
    beroep,
    contactpersoon,
    functieTitel,
    handigeWeetjes,
    introTekst,
    plaatsTewerkstelling,
    sollicitatieLink,
    vacatureTekst,
    videoType,
    videoUrl,
  } = vacancy;
  const { t } = useTranslation(['jobs']);
  const [currentPath, setCurrentPath] = useState('');

  useEffect(() => {
    setCurrentPath(window.location.href);
  }, []);

  const vacancyText = htmlStringToDocument(vacatureTekst);
  const hasImage = Boolean(beroep?.[0]?.url);
  const hasVideo = Boolean(videoUrl && videoType === 'youtube');
  const hasFullAddress =
    !!plaatsTewerkstelling &&
    !!plaatsTewerkstelling?.straatnaam &&
    !!plaatsTewerkstelling?.huisnummer &&
    !!plaatsTewerkstelling?.postcode &&
    !!plaatsTewerkstelling?.gemeente &&
    !!plaatsTewerkstelling?.land;

  return (
    <div className="relative my-4 md:my-8">
      <div className="mb-8">
        <Link className="cursor-pointer" href={vacanciesSlugs[locale]}>
          <span className="text-brand">
            <FontAwesomeIcon icon={faChevronLeft} />
          </span>
          <span className="ml-2">{t('toOverview')}</span>
        </Link>
      </div>
      <div className="mb-5">
        <div className="mb-4 flex flex-row items-center justify-end gap-4 md:hidden">
          <span>{t('socialShare')}</span>
          <span className="text-brand">
            <SocialShare
              body={t('summary', { link: currentPath }) as string}
              facebook
              linkedIn
              mail
              title={functieTitel}
              url={currentPath}
            />
          </span>
        </div>
        <div className="mb-4 md:mb-12">
          <h1 className="font-gotham">{functieTitel}</h1>
        </div>
        <div className="my-4 flex w-full flex-col justify-between gap-2 md:mb-12 md:flex-row md:items-center md:gap-3">
          <div className="flex flex-col gap-2 md:flex-row md:gap-4">
            {plaatsTewerkstelling?.locatie && (
              <span className="flex flex-row items-center gap-2 font-bold">
                <span className="text-brand">
                  <FontAwesomeIcon icon={faLocationDot} width={16} />
                </span>
                {plaatsTewerkstelling.locatie}
              </span>
            )}
            {handigeWeetjes?.contract && <span>{handigeWeetjes?.contract}</span>}
          </div>
          <div className="hidden flex-row items-center gap-4 md:flex">
            <span>{t('socialShare')}</span>
            <span className="text-brand">
              <SocialShare
                body={t('summary', { link: currentPath }) as string}
                facebook
                linkedIn
                mail
                title={functieTitel}
                url={currentPath}
              />
            </span>
          </div>
        </div>
        <div className="flex w-full flex-col gap-4 md:flex-row">
          <div className={MainDivStyling({ hasAsset: hasImage || hasVideo || hasFullAddress })}>
            {introTekst && (
              <div>
                {introTekst}
                <ApplyButton sollicitatieLink={sollicitatieLink} />
              </div>
            )}
            <RichText content={vacancyText} mapperOptions={{ locale }} />
          </div>
          {(hasImage || hasVideo || hasFullAddress) && (
            <div className={AssetDivStyling({ hasAsset: hasImage || hasVideo || hasFullAddress })}>
              {hasImage && (
                <Image
                  alt={beroep?.[0]?.altTekst ?? ''}
                  className="h-auto"
                  src={beroep?.[0]?.url ?? ''}
                  wrapperClassName="h-auto"
                />
              )}
              {hasVideo && <Video src={videoUrl ?? ''} />}
              {hasFullAddress && (
                <div className="aspect-square w-full">
                  <LocationMap
                    address={{
                      street: plaatsTewerkstelling?.straatnaam,
                      number: plaatsTewerkstelling?.huisnummer,
                      zipcode: plaatsTewerkstelling?.postcode,
                      city: plaatsTewerkstelling?.gemeente,
                      country: plaatsTewerkstelling?.land,
                    }}
                    location={plaatsTewerkstelling?.locatie}
                  />
                </div>
              )}
            </div>
          )}
        </div>
        <ApplyButton className="block w-fit" sollicitatieLink={sollicitatieLink ?? spontaneousApplicationLink} />
      </div>
      {aboutSections && (
        <div className="mb-14">
          {aboutSections.map(entry => (
            <ComponentMapper entry={entry} key={entry.id} locale={locale} />
          ))}
        </div>
      )}
      {contactpersoon?.contactformulier && <JobDetailPromotionCta contactpersoon={contactpersoon} />}
      {enrichedSections && (
        <div className="mb-14">
          {enrichedSections.map(entry => (
            <ComponentMapper entry={entry} key={entry.id} locale={locale} />
          ))}
        </div>
      )}
    </div>
  );
};

export default JobsDetailPage;
